import { StrictMode, useEffect } from 'react';
import { createRoot } from 'react-dom/client';
import { HelmetProvider } from 'react-helmet-async';
import { BrowserRouter } from 'react-router-dom';
import { Analytics } from '@vercel/analytics/react';
import { SpeedInsights } from '@vercel/speed-insights/react';

import App from './App';
import { ErrorBoundary } from '@/components/shared/ErrorBoundary';
import { ScrollRestoration, ScrollToTopButton } from '@/components/shared/ScrollToTop';
import { CookieConsent } from '@/components/shared/CookieConsent';
import { initializeAnalytics, hasAnalyticsConsent } from '@/lib/analytics';
import '@/styles/globals.css';
import '@/styles/print.css';

/**
 * Root component that wraps the app with providers and global components
 */
function Root() {
  // Initialize analytics on mount if consent is already given
  useEffect(() => {
    if (hasAnalyticsConsent()) {
      initializeAnalytics(true);
    }
  }, []);

  return (
    <ErrorBoundary>
      <HelmetProvider>
        <BrowserRouter>
          {/* Scroll to top on route change */}
          <ScrollRestoration />

          {/* Main application */}
          <App />

          {/* Scroll to top button */}
          <ScrollToTopButton threshold={400} />

          {/* Cookie consent banner */}
          <CookieConsent
            privacyPolicyUrl="/privacy"
            position="bottom"
            showPreferencesButton
            consentExpiryDays={365}
          />

          {/*
           * Vercel Web Analytics (cookieless page views/visitors) and Speed
           * Insights (real-user Core Web Vitals). Both are first-party and do
           * not set cookies, so they run independently of the GA4 consent gate
           * above. Beacons post to /_vercel/insights/* and /_vercel/speed-insights/*.
           */}
          <Analytics />
          <SpeedInsights />
        </BrowserRouter>
      </HelmetProvider>
    </ErrorBoundary>
  );
}

// Mount the application
const rootElement = document.getElementById('root');

if (!rootElement) {
  throw new Error('Root element not found. Make sure there is a <div id="root"></div> in index.html');
}

createRoot(rootElement).render(
  <StrictMode>
    <Root />
  </StrictMode>
);
